import { useState, useEffect } from "react";
import { Grid, TextField, Typography, useTheme, Box } from "@mui/material";
import { KeyboardArrowDown, KeyboardArrowUp } from "@mui/icons-material";
import { Controller, useForm } from "react-hook-form";

import { ButtonV1, DropdownV1, DatePickerV1 } from "components";
import { ErrorMsg } from "CustomStyledComponents";

import SearchableDropdownV1 from "./SearchableDropdown";

import { CHANNEL_OPTIONS, TO_DATE_ERROR, FROM_DATE_ERROR } from "../constant";

import { formatDate } from "../makeData";

const CustomTextField = ({
  name,
  label,
  control,
  digitsOnly = false,
  maxLength,
}) => {
  return (
    <Controller
      name={name}
      control={control}
      render={({ field }) => (
        <TextField
          {...field}
          size="small"
          fullWidth
          label={label}
          inputProps={{ maxLength }}
          onChange={(e) => {
            const value = digitsOnly
              ? e.target.value.replace(/\D/g, "")
              : e.target.value;

            field.onChange(value);
          }}
        />
      )}
    />
  );
};

const defaultChannel = CHANNEL_OPTIONS.email;

const DAYS_30 = 30 * 24 * 60 * 60 * 1000;
const Filter = ({ onSubmit, onReset, templates, getProviderStatusOptions }) => {
  const theme = useTheme();
  const [toggleFilter, setToggleFilter] = useState(false);
  const [selectedChannel, setSelectedChannel] = useState(defaultChannel);
  const [dateError, setDateError] = useState("");

  const {
    control,
    handleSubmit,
    reset,
    setValue,
    watch: watchForm,
  } = useForm({
    mode: "onTouched",
    defaultValues: {
      receiver: "",
      fromDate: formatDate(new Date(Date.now() - DAYS_30)),
      toDate: formatDate(new Date()),
      channel: CHANNEL_OPTIONS.email,
      template_type: "",
      provider_status: "",
      event_id: "",
      reference: "",
    },
  });

  const fromDate = watchForm("fromDate");
  const toDate = watchForm("toDate");

  useEffect(() => {
    // Validate dates whenever they change
    if (fromDate && toDate) {
      const fromDateTime = new Date(fromDate).getTime();
      const toDateTime = new Date(toDate).getTime();

      if (fromDateTime > toDateTime) {
        setDateError(FROM_DATE_ERROR);
      } else {
        setDateError("");
      }
    }
  }, [fromDate, toDate]);

  const handleFilter = (data) => {
    // Check if there's a date error
    if (dateError) {
      return;
    }

    onSubmit({
      ...data,
      toDate: formatDate(data.toDate),
    });
  };

  const handleReset = () => {
    reset({
      receiver: "",
      fromDate: formatDate(new Date(Date.now() - DAYS_30)),
      toDate: formatDate(new Date()),
      channel: CHANNEL_OPTIONS.email,
      template_type: "",
      provider_status: "",
      event_id: "",
      reference: "",
    });
    setSelectedChannel(CHANNEL_OPTIONS.email);
    setDateError("");
    onReset();
  };

  useEffect(() => {
    setValue("receiver", "");
    setValue("provider_status", "");
  }, [selectedChannel, setValue]);

  return (
    <Box
      sx={{
        border: `1px solid ${theme.palette.grey["400"]}`,
        borderRadius: 2,
        padding: 3,
      }}
    >
      <Box p={2} as="form" onSubmit={handleSubmit(handleFilter)}>
        <Grid container gap={3}>
          <Grid container xs={12} spacing={4}>
            <Grid item xs={2.4}>
              <Box>
                <Controller
                  name="fromDate"
                  control={control}
                  render={({ field }) => (
                    <DatePickerV1
                      value={field.value}
                      onChange={field.onChange}
                      disableFuture={true}
                      label="From date"
                      inputProps={{ placeholder: "Date" }}
                      dateStyle={{ width: "100%" }}
                    />
                  )}
                />
                {dateError && <ErrorMsg>{dateError}</ErrorMsg>}
              </Box>
            </Grid>
            <Grid item xs={2.4}>
              <Box>
                <Controller
                  name="toDate"
                  control={control}
                  render={({ field }) => (
                    <DatePickerV1
                      value={field.value}
                      onChange={field.onChange}
                      label="To date"
                      disableFuture={true}
                      inputProps={{ placeholder: "Date" }}
                      dateStyle={{ width: "100%" }}
                    />
                  )}
                />
                {dateError && <ErrorMsg>{TO_DATE_ERROR}</ErrorMsg>}
              </Box>
            </Grid>
            <Grid item xs={2.4}>
              <Controller
                name="channel"
                control={control}
                render={({ field }) => (
                  <DropdownV1
                    {...field}
                    placeholder="Select channel"
                    itemList={Object.values(CHANNEL_OPTIONS)}
                    listType="array"
                    onChange={(e) => {
                      const channel = e.target.value;
                      field.onChange(channel);
                      setSelectedChannel(channel);
                    }}
                  />
                )}
              />
            </Grid>
            <Grid item xs={2.4}>
              {selectedChannel === "EMAIL" ? (
                <CustomTextField
                  name="receiver"
                  label="Enter email id"
                  control={control}
                />
              ) : (
                <CustomTextField
                  name="receiver"
                  label="Enter mobile no"
                  control={control}
                  digitsOnly={true}
                  maxLength={10}
                />
              )}
            </Grid>
            <Grid item xs={2.4}>
              <Controller
                name="template_type"
                control={control}
                render={({ field }) => (
                  <SearchableDropdownV1
                    value={field.value}
                    onChange={field.onChange}
                    placeholder="Select template type"
                    itemList={templates}
                    listType="array"
                  />
                )}
              />
            </Grid>

            {toggleFilter && (
              <>
                <Grid item xs={2.4}>
                  <CustomTextField
                    name="reference"
                    label="Order number"
                    control={control}
                  />
                </Grid>
                <Grid item xs={2.4}>
                  <Controller
                    name="provider_status"
                    control={control}
                    render={({ field }) => (
                      <DropdownV1
                        {...field}
                        placeholder="Provider status"
                        itemList={getProviderStatusOptions(selectedChannel)}
                        listType="object"
                      />
                    )}
                  />
                </Grid>
                <Grid item xs={2.4}>
                  <CustomTextField
                    name="event_id"
                    label="Event ID"
                    control={control}
                  />
                </Grid>
              </>
            )}
          </Grid>
          <Grid container xs={12} alignItems="center">
            <Grid item xs={3}>
              <Box display="flex" gap={2}>
                <ButtonV1
                  title="Reset"
                  variant="outlined"
                  onClick={handleReset}
                />
                <ButtonV1 title="Submit" type="submit" variant="contained" />
              </Box>
            </Grid>
            <Grid
              item
              xs={9}
              display="flex"
              justifyContent="flex-end"
              alignItems="center"
              gap={1}
              sx={{
                cursor: "pointer",
                color: theme.palette.primary.main,
              }}
              onClick={() => setToggleFilter((p) => !p)}
            >
              <Typography fontWeight={600}>
                {toggleFilter
                  ? "Close advance filters"
                  : "Open advance filters"}
              </Typography>
              {toggleFilter ? <KeyboardArrowUp /> : <KeyboardArrowDown />}
            </Grid>
          </Grid>
        </Grid>
      </Box>
    </Box>
  );
};

export default Filter;
